import {
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import http from "../../../utils/http";
import { LabelStyle } from "../../Common/styles/form";

type CanonicalQuestion = {
  id: string;
  label: string;
  type: string;
};

type Props = {
  question: any;
  onChange: Function;
  isPreview: boolean;
};

const CanonicalQuestionMapper = ({ question, onChange, isPreview }: Props) => {
  const [enabled, setEnabled] = useState(!!question.canonicalQuestionId);
  const [options, setOptions] = useState<CanonicalQuestion[]>([]);
  const [selected, setSelected] = useState<CanonicalQuestion | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the currently-linked canonical question on mount
  useEffect(() => {
    if (!question.canonicalQuestionId) return;
    http
      .get(`/admin/registry/canonical-questions/${question.canonicalQuestionId}`)
      .then((res) => {
        const cq = res.data?.data;
        if (cq) {
          setSelected({ id: cq.id, label: cq.label, type: cq.type });
          setInputValue(cq.label);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOptions = (search: string) => {
    setLoading(true);
    http
      .get("/admin/registry/canonical-questions", {
        params: { search, type: question.type, size: 50 },
      })
      .then((res) => {
        const rows: CanonicalQuestion[] = (res.data?.data?.data ?? []).map(
          (cq: any) => ({ id: cq.id, label: cq.label, type: cq.type })
        );
        setOptions(rows);
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  };

  const handleEnabledChange = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      setSelected(null);
      setInputValue("");
      setOptions([]);
      onChange("canonicalQuestionId", null);
    } else {
      fetchOptions("");
    }
  };

  const handleInputChange = (_: any, value: string) => {
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(value), 300);
  };

  const handleSelect = (_: any, value: CanonicalQuestion | null) => {
    setSelected(value);
    onChange("canonicalQuestionId", value?.id ?? null);
  };

  return (
    <Stack gap={2}>
      <FormControlLabel
        disabled={isPreview}
        control={
          <Checkbox
            checked={enabled}
            onChange={(e) => handleEnabledChange(e.target.checked)}
          />
        }
        label={
          <Typography variant="subtitle1" fontWeight="medium">
            Map to canonical question
          </Typography>
        }
      />
      {enabled && (
        <Stack>
          <Typography sx={LabelStyle}>Canonical question</Typography>
          <Autocomplete
            disabled={isPreview}
            options={options}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={selected}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleSelect}
            loading={loading}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search canonical questions…"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default CanonicalQuestionMapper;
